/**
 * usePaymentMethods Hook
 *
 * 결제수단 목록을 조회하는 Hook입니다.
 */

import { useState, useEffect } from 'react'
import { paymentMethodRepository } from '../repositories'
import type { PaymentMethod } from '../types/PaymentMethod'

interface UsePaymentMethodsReturn {
  /**
   * 결제수단 목록 (활성화된 것만)
   */
  paymentMethods: PaymentMethod[]

  /**
   * 로딩 상태
   */
  loading: boolean

  /**
   * 에러 정보
   */
  error: Error | null

  /**
   * 데이터 재조회
   */
  refetch: () => Promise<void>
}

/**
 * 결제수단 목록 조회 Hook
 *
 * 기본적으로 활성화된 결제수단만 반환합니다.
 *
 * @returns 결제수단 목록, 로딩 상태, 에러, refetch 함수
 *
 * @example
 * ```tsx
 * function PaymentMethodSelector() {
 *   const { paymentMethods, loading, error } = usePaymentMethods()
 *
 *   if (loading) return <div>로딩 중...</div>
 *   if (error) return <div>에러: {error.message}</div>
 *
 *   return (
 *     <select>
 *       {paymentMethods.map(pm => (
 *         <option key={pm.id} value={pm.id}>{pm.name}</option>
 *       ))}
 *     </select>
 *   )
 * }
 * ```
 */
export function usePaymentMethods(): UsePaymentMethodsReturn {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true)
      setError(null)

      // 활성화된 결제수단만 조회
      const data = await paymentMethodRepository.findActive()
      setPaymentMethods(data)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('결제수단 목록을 불러오는데 실패했습니다')
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPaymentMethods()
  }, [])

  return {
    paymentMethods,
    loading,
    error,
    refetch: fetchPaymentMethods,
  }
}
